import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { ReactComponent as WifiIcon } from "../../../assets/icons/wifi.svg";
import { twMerge } from "tailwind-merge";

export type ConnectionQuality = "excellent" | "fair" | "poor";

/**
 * `null` represents the pre-measurement state, before the first latency probe
 * has resolved. We deliberately avoid defaulting to a concrete quality (e.g.
 * "excellent") so the UI never shows reassuring scoring copy without evidence.
 */
export type ConnectionQualityState = ConnectionQuality | null;

type QualityConfig = {
  label: string;
  title: string;
  description: string;
  /** Text/icon accent color. */
  accent: string;
  /** Light tint used behind the status badge. */
  badgeBg: string;
};

/** Neutral state shown until the first probe resolves. */
const MEASURING_CONFIG: QualityConfig = {
  label: "Checking…",
  title: "Checking connection",
  description: "Measuring your connection quality…",
  accent: "#71717A",
  badgeBg: "#F4F4F5",
};

const QUALITY_CONFIG: Record<ConnectionQuality, QualityConfig> = {
  excellent: {
    label: "Excellent",
    title: "Excellent connection",
    description: "Signal is strong and stable. This session will score normally.",
    accent: "#15803D",
    badgeBg: "#F0FDF4",
  },
  fair: {
    label: "Fair",
    title: "Fair Connection",
    description:
      "Looks like your connection could be better. Try a different location to ensure a smooth PraxisPlay session.",
    accent: "#CA8A04",
    badgeBg: "#FEFCE8",
  },
  poor: {
    label: "Poor",
    title: "Poor connection",
    description:
      "Uh oh! Looks like your connection is weak. If the call drops, this session will not be scored.",
    accent: "#DC2626",
    badgeBg: "#FDF0F096",
  },
};

/** RTT (ms) thresholds mapping a measured latency to a quality bucket. */
const EXCELLENT_MAX_RTT = 150;
const FAIR_MAX_RTT = 450;
const PROBE_INTERVAL_MS = 5000;
const PROBE_TIMEOUT_MS = 4000;

function rttToQuality(rtt: number): ConnectionQuality {
  if (rtt <= EXCELLENT_MAX_RTT) return "excellent";
  if (rtt <= FAIR_MAX_RTT) return "fair";
  return "poor";
}

/**
 * Measures connection quality with a lightweight latency probe against a
 * same-origin asset. Works in every browser (unlike the Network Information
 * API, which is unavailable in Safari/Firefox) while still reading
 * navigator.connection as an early hint when present.
 *
 * When `enabled` is false the hook performs no probing and registers no
 * listeners, so a hidden/inactive consumer never triggers background network
 * requests. Toggling `enabled` starts or fully tears down measurement.
 */
export function useConnectionQuality(enabled = true): ConnectionQualityState {
  const [quality, setQuality] = useState<ConnectionQualityState>(null);
  const cancelledRef = useRef(false);
  /**
   * Monotonic id identifying the most recently started run. Probes and the
   * offline handler can race (interval, initial run, and online/offline
   * events all trigger concurrently), so each run captures its id and only
   * writes state while it is still the latest — preventing an older probe
   * that resolves late from overwriting a newer result with a stale value.
   */
  const latestRunRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;

    const probe = async () => {
      const runId = ++latestRunRef.current;
      const commit = (value: ConnectionQuality) => {
        if (!cancelledRef.current && runId === latestRunRef.current) {
          setQuality(value);
        }
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        commit("poor");
        return;
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
      const start =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      try {
        await fetch(`${window.location.origin}/robots.txt?_=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        const rtt =
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          start;
        commit(rttToQuality(rtt));
      } catch {
        commit("poor");
      } finally {
        clearTimeout(timeout);
      }
    };

    probe();
    const interval = setInterval(probe, PROBE_INTERVAL_MS);
    const onOffline = () => {
      // Invalidate any in-flight probe so it cannot overwrite the offline state.
      latestRunRef.current++;
      setQuality("poor");
    };
    const onOnline = () => {
      // Clear the stale "poor" state immediately so the popover doesn't keep
      // showing poor-connection copy (e.g. "session will not be scored") while
      // the fresh probe is still in flight. Fall back to the neutral measuring
      // state until the new probe resolves with a real measurement.
      setQuality(null);
      probe();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      cancelledRef.current = true;
      clearInterval(interval);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled]);

  return quality;
}

const shellTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const },
};
const crossfadeTransition = { duration: 0.12, ease: [0.22, 1, 0.36, 1] as const };

type InternetConnectionPopoverProps = {
  className?: string;
  /** When false, nothing is rendered. */
  visible?: boolean;
};

export function InternetConnectionPopover({
  className,
  visible = true,
}: InternetConnectionPopoverProps) {
  const panelId = useId();
  const quality = useConnectionQuality(visible);
  const config = quality ? QUALITY_CONFIG[quality] : MEASURING_CONFIG;

  const [panel, setPanel] = useState<"collapsed" | "expanded">("collapsed");
  const [shellCard, setShellCard] = useState(false);
  const panelRef = useRef(panel);
  panelRef.current = panel;

  const open = useCallback(() => setPanel("expanded"), []);
  const close = useCallback(() => setPanel("collapsed"), []);

  const onInnerExitComplete = useCallback(() => {
    setShellCard(panelRef.current === "expanded");
  }, []);

  if (!visible) return null;

  const badge = (
    <span
      className="inline-flex items-center gap-1.5 h-7 rounded-full px-2.5 py-1"
      style={{ backgroundColor: config.badgeBg }}
    >
      <WifiIcon className="h-[15px] w-[21px]" style={{ color: config.accent }} />
      <span
        className="hidden sm:inline text-xs font-semibold"
        style={{ color: config.accent }}
      >
        {config.label}
      </span>
    </span>
  );

  return (
    <div className={twMerge("pointer-events-auto", className)}>
      <motion.div
        layout
        initial={false}
        transition={shellTransition}
        className={twMerge(
          "bg-[#FCFAF8] text-zinc-800",
          shellCard
            ? "rounded-[26px] shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18)] w-[min(100vw-2rem,320px)]"
            : "rounded-full shadow-[0_2px_12px_-4px_rgba(0,0,0,0.12)]",
        )}
      >
        <AnimatePresence initial={false} mode="wait" onExitComplete={onInnerExitComplete}>
          {panel === "collapsed" ? (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={crossfadeTransition}
            >
              <button
                type="button"
                className="flex items-center h-7"
                aria-expanded={false}
                aria-controls={panelId}
                aria-label={`${config.label} connection. Show details`}
                onClick={open}
              >
                {badge}
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="expanded"
              id={panelId}
              role="region"
              aria-label={config.title}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={crossfadeTransition}
              className="min-w-0 px-4 relative py-4"
            >
              <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
                {badge}
                <button
                  type="button"
                  className="hover:opacity-80 transition-opacity text-zinc-800 shrink-0"
                  aria-label="Close connection details"
                  onClick={close}
                >
                  <CloseIcon className="w-5 h-5 [&_path]:stroke-current" />
                </button>
              </div>
              <h2
                className="text-xl font-semibold leading-tight break-words mb-2"
                style={{ color: config.accent }}
              >
                {config.title}
              </h2>
              <p className="text-sm font-normal text-gray-701 leading-snug break-words mb-4">
                {config.description}
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="rounded-full bg-gray-201 hover:opacity-80 transition-colors px-4 py-2 text-xs text-black"
                  onClick={close}
                >
                  Got it
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
